import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc, 
  getDocs, 
  query, 
  runTransaction,
  where, 
  orderBy,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CategoryService } from './categoryService';

// Service pour gérer les produits et codes
export class ProductService {
  static hasTopupMetadata(product = {}) {
    return Boolean(
      String(product.subcategoryId || '').trim() ||
      String(product.subcategoryName || '').trim() ||
      String(product.topupTemplateId || '').trim() ||
      String(product.topupTemplateName || '').trim() ||
      String(product.topupFulfillmentMode || '').trim()
    );
  }

  static resolveProductSection(product = {}, categories = []) {
    const normalizedExplicitSection = String(product.section || '').trim();
    const normalizedCategoryName = CategoryService.normalizeCategory(product.category);
    const categoryById = categories.find((category) => category.id === product.categoryId) || null;
    const subcategoryById = categories.find((category) => category.id === product.subcategoryId) || null;
    const categoriesByName = categories.filter((category) => (
      CategoryService.normalizeCategory(category.name || category.id) === normalizedCategoryName
    ));
    const hasTopupMetadata = this.hasTopupMetadata(product);

    if (CategoryService.isTopupSubcategory(subcategoryById)) {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    if (hasTopupMetadata) {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    if (categoryById) {
      return CategoryService.matchesSection(categoryById, CategoryService.CATEGORY_SECTIONS.TOPUP)
        ? CategoryService.CATEGORY_SECTIONS.TOPUP
        : CategoryService.CATEGORY_SECTIONS.CARDS;
    }

    if (normalizedExplicitSection === CategoryService.CATEGORY_SECTIONS.TOPUP) {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    if (normalizedExplicitSection === CategoryService.CATEGORY_SECTIONS.CARDS) {
      return CategoryService.CATEGORY_SECTIONS.CARDS;
    }

    if (categoriesByName.length === 1) {
      return CategoryService.matchesSection(categoriesByName[0], CategoryService.CATEGORY_SECTIONS.TOPUP)
        ? CategoryService.CATEGORY_SECTIONS.TOPUP
        : CategoryService.CATEGORY_SECTIONS.CARDS;
    }

    return CategoryService.CATEGORY_SECTIONS.CARDS;
  }

  static filterProductsBySection(products = [], categories = [], section = CategoryService.CATEGORY_SECTIONS.CARDS) {
    return products.filter((product) => this.resolveProductSection(product, categories) === section);
  }

  static normalizeCodeValue(codeValue = '') {
    return String(codeValue || '').trim();
  }

  static async buildCodeDocumentId(codeValue = '') {
    const normalizedCode = this.normalizeCodeValue(codeValue);
    if (!normalizedCode) {
      throw new Error('Code invalide');
    }

    const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;

    if (cryptoApi?.subtle && typeof TextEncoder !== 'undefined') {
      const encodedValue = new TextEncoder().encode(normalizedCode);
      const digest = await cryptoApi.subtle.digest('SHA-256', encodedValue);
      const hashArray = Array.from(new Uint8Array(digest));
      return `code_${hashArray.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
    }

    let hash = 2166136261;
    for (let index = 0; index < normalizedCode.length; index += 1) {
      hash ^= normalizedCode.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return `code_${(hash >>> 0).toString(16).padStart(8, '0')}_${normalizedCode.length}`;
  }

  
  // Récupérer tous les produits
  static async getAllProducts() {
    try {
      const productsQuery = query(
        collection(db, 'products'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  }
  
  // Récupérer les produits actifs pour les revendeurs
  static async getActiveProducts() {
    try {
      const productsQuery = query(
        collection(db, 'products'),
        where('status', '==', 'active'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(productsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des produits actifs:', error);
      throw error;
    }
  }
  
  // Récupérer un produit par son ID
  static async getProductById(productId) {
    try {
      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);
      
      if (!productDoc.exists()) {
        return null;
      }
      
      return {
        id: productDoc.id,
        ...productDoc.data()
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du produit:', error);
      throw error;
    }
  }
  
  // Créer un nouveau produit
  static async createProduct(productData) {
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        status: productData.status || 'active',
        offers: productData.offers || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création du produit:', error);
      throw error;
    }
  }
  
  // Mettre à jour un produit
  static async updateProduct(productId, updates) {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      throw error;
    }
  }
  
  // Supprimer un produit
  static async deleteProduct(productId) {
    try {
      const productRef = doc(db, 'products', productId);
      await deleteDoc(productRef);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du produit:', error);
      throw error;
    }
  }
  
  // Ajouter une offre à un produit
  static async addOfferToProduct(productId, offerData) {
    try {
      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);
      
      if (!productDoc.exists()) {
        throw new Error('Produit introuvable');
      }
      
      const product = productDoc.data();
      const offers = product.offers || [];
      
      const newOffer = {
        id: doc(collection(db, 'temp')).id, // Générer un ID unique
        ...offerData,
        stock: 0,
        createdAt: new Date()
      };
      
      offers.push(newOffer);
      
      await updateDoc(productRef, {
        offers,
        updatedAt: serverTimestamp()
      });
      
      return newOffer.id;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'offre:', error);
      throw error;
    }
  }
  
  // Importer des codes en lot (CSV)
  static async importCodes(productId, offerId, codes) {
    try {
      const importedCodes = [];
      
      // Vérifier que le produit et l'offre existent
      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);
      
      if (!productDoc.exists()) {
        throw new Error('Produit introuvable');
      }
      
      const product = productDoc.data();
      const offer = product.offers?.find(o => o.id === offerId);
      
      if (!offer) {
        throw new Error('Offre introuvable');
      }

      const seenInBatch = new Set();
      const sanitizedCodeEntries = [];
      const skippedDuplicates = [];
      
      // Créer les documents de codes
      for (const codeValue of codes) {
        const normalizedCode = this.normalizeCodeValue(codeValue);

        if (!normalizedCode) {
          continue;
        }

        if (seenInBatch.has(normalizedCode)) {
          skippedDuplicates.push(normalizedCode);
          continue;
        }

        seenInBatch.add(normalizedCode);
        const codeId = await this.buildCodeDocumentId(normalizedCode);
        sanitizedCodeEntries.push({
          id: codeId,
          code: normalizedCode
        });
      }

      if (sanitizedCodeEntries.length === 0) {
        return {
          success: true,
          importedCount: 0,
          skippedCount: skippedDuplicates.length,
          duplicates: skippedDuplicates,
          codes: []
        };
      }

      await runTransaction(db, async (transaction) => {
        const productSnapshot = await transaction.get(productRef);
        if (!productSnapshot.exists()) {
          throw new Error('Produit introuvable');
        }

        const liveProduct = productSnapshot.data() || {};
        const liveOffers = Array.isArray(liveProduct.offers) ? liveProduct.offers : [];
        const liveOffer = liveOffers.find((entry) => entry.id === offerId);

        if (!liveOffer) {
          throw new Error('Offre introuvable');
        }

        const codeRefs = sanitizedCodeEntries.map((codeEntry) => ({
          codeEntry,
          ref: doc(db, 'codes', codeEntry.id)
        }));
        const codeSnapshots = await Promise.all(
          codeRefs.map(({ ref }) => transaction.get(ref))
        );

        let importedCount = 0;
        const codesToCreate = [];

        codeRefs.forEach(({ codeEntry, ref }, index) => {
          const codeSnapshot = codeSnapshots[index];

          if (codeSnapshot.exists()) {
            skippedDuplicates.push(codeEntry.code);
            return;
          }

          codesToCreate.push({
            ref,
            codeEntry
          });
        });

        codesToCreate.forEach(({ ref, codeEntry }) => {
          transaction.set(ref, {
            code: codeEntry.code,
            productId,
            offerId,
            productName: liveProduct.name,
            offerName: liveOffer.name,
            status: 'available',
            createdAt: serverTimestamp()
          });

          importedCodes.push(codeEntry);
          importedCount += 1;
        });

        if (importedCount === 0) {
          return;
        }

        const updatedOffers = liveOffers.map((entry) => (
          entry.id === offerId
            ? { ...entry, stock: Number(entry.stock || 0) + importedCount }
            : entry
        ));

        transaction.update(productRef, {
          offers: updatedOffers,
          updatedAt: serverTimestamp()
        });
      });
      
      return {
        success: true,
        importedCount: importedCodes.length,
        skippedCount: skippedDuplicates.length,
        duplicates: skippedDuplicates,
        codes: importedCodes
      };
    } catch (error) {
      console.error('Erreur lors de l\'importation des codes:', error);
      throw error;
    }
  }
  
  // Récupérer les codes d'une offre
  static async getOfferCodes(productId, offerId) {
    try {
      const codesQuery = query(
        collection(db, 'codes'),
        where('productId', '==', productId),
        where('offerId', '==', offerId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(codesQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des codes:', error);
      throw error;
    }
  }

  static async deleteOfferCode(productId, offerId, codeId) {
    try {
      const productRef = doc(db, 'products', productId);
      const codeRef = doc(db, 'codes', codeId);

      await runTransaction(db, async (transaction) => {
        const [productSnapshot, codeSnapshot] = await Promise.all([
          transaction.get(productRef),
          transaction.get(codeRef)
        ]);

        if (!productSnapshot.exists()) {
          throw new Error('Produit introuvable');
        }

        if (!codeSnapshot.exists()) {
          throw new Error('Code introuvable');
        }

        const codeData = codeSnapshot.data() || {};
        if (codeData.productId !== productId || codeData.offerId !== offerId) {
          throw new Error('Ce code ne correspond pas a cette offre');
        }

        if (codeData.status !== 'available') {
          throw new Error('Seuls les codes non vendus peuvent etre supprimes');
        }

        const productData = productSnapshot.data() || {};
        const offers = Array.isArray(productData.offers) ? productData.offers : [];
        const targetOffer = offers.find((entry) => entry.id === offerId);

        if (!targetOffer) {
          throw new Error('Offre introuvable');
        }

        const updatedOffers = offers.map((entry) => (
          entry.id === offerId
            ? { ...entry, stock: Math.max(0, Number(entry.stock || 0) - 1) }
            : entry
        ));

        transaction.delete(codeRef);
        transaction.update(productRef, {
          offers: updatedOffers,
          updatedAt: serverTimestamp()
        });
      });

      return await this.getProductById(productId);
    } catch (error) {
      console.error('Erreur lors de la suppression du code:', error);
      throw error;
    }
  }
  
  // Récupérer les statistiques des codes
  static async getCodesStats(productId, offerId) {
    try {
      const codesQuery = query(
        collection(db, 'codes'),
        where('productId', '==', productId),
        where('offerId', '==', offerId)
      );
      
      const snapshot = await getDocs(codesQuery);
      const codes = snapshot.docs.map(doc => doc.data());
      
      const stats = {
        total: codes.length,
        available: codes.filter(c => c.status === 'available').length,
        reserved: codes.filter(c => c.status === 'reserved').length,
        sold: codes.filter(c => c.status === 'sold').length
      };
      
      return stats;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  static async syncProductOfferStocks(productId, productData = null) {
    try {
      const product = productData || await this.getProductById(productId);

      if (!product) {
        throw new Error('Produit introuvable');
      }

      const offers = Array.isArray(product.offers) ? product.offers : [];
      if (offers.length === 0) {
        return product;
      }

      const codesQuery = query(
        collection(db, 'codes'),
        where('productId', '==', productId)
      );

      const snapshot = await getDocs(codesQuery);
      const codes = snapshot.docs.map((docSnapshot) => docSnapshot.data() || {});
      const availableCountByOffer = new Map();

      codes.forEach((codeEntry) => {
        if (codeEntry.status !== 'available') {
          return;
        }

        const currentValue = availableCountByOffer.get(codeEntry.offerId) || 0;
        availableCountByOffer.set(codeEntry.offerId, currentValue + 1);
      });

      let hasChanges = false;
      const updatedOffers = offers.map((offer) => {
        const nextStock = availableCountByOffer.get(offer.id) || 0;
        const currentStock = Number(offer?.stock || 0);

        if (currentStock !== nextStock) {
          hasChanges = true;
        }

        return {
          ...offer,
          stock: nextStock
        };
      });

      if (hasChanges) {
        await updateDoc(doc(db, 'products', productId), {
          offers: updatedOffers,
          updatedAt: serverTimestamp()
        });
      }

      return {
        ...product,
        offers: updatedOffers
      };
    } catch (error) {
      console.error('Erreur lors de la synchronisation du stock produit:', error);
      throw error;
    }
  }

  static async syncAllCardProductStocks() {
    try {
      const [products, categories] = await Promise.all([
        this.getAllProducts(),
        CategoryService.getAllCategories()
      ]);
      const cardProducts = this.filterProductsBySection(
        products,
        categories,
        CategoryService.CATEGORY_SECTIONS.CARDS
      );

      const results = [];

      for (const product of cardProducts) {
        const syncedProduct = await this.syncProductOfferStocks(product.id, product);
        const hasChanges = JSON.stringify(product.offers || []) !== JSON.stringify(syncedProduct.offers || []);

        results.push({
          productId: product.id,
          productName: product.name,
          changed: hasChanges
        });
      }

      return {
        total: results.length,
        changed: results.filter((entry) => entry.changed).length,
        results
      };
    } catch (error) {
      console.error('Erreur lors de la synchronisation globale des stocks cartes:', error);
      throw error;
    }
  }
  
  // Nettoyer les réservations expirées
  static async cleanExpiredReservations() {
    try {
      const now = new Date();
      const expiredQuery = query(
        collection(db, 'codes'),
        where('status', '==', 'reserved'),
        where('ttl', '<=', now)
      );
      
      const snapshot = await getDocs(expiredQuery);
      const batch = writeBatch(db);
      
      snapshot.docs.forEach((doc) => {
        batch.update(doc.ref, {
          status: 'available',
          reservedBy: null,
          reservedAt: null,
          reservationId: null,
          ttl: null
        });
      });
      
      await batch.commit();
      
      return snapshot.size;
    } catch (error) {
      console.error('Erreur lors du nettoyage des réservations:', error);
      throw error;
    }
  }
}
